import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Select,
  Group,
  Stack,
  Badge,
  Alert,
  Button,
  Text,
  Code,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import {
  DetailDrawer,
  DetailSection,
  DetailField,
} from "../../components/common/DetailDrawer";
import { SearchInput } from "../../components/common/SearchInput";
import { taxReceiptsApi } from "../../api/settlements";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { errorMessage } from "../../lib/axios";
import { formatMoney, formatDateTime } from "../../lib/format";
import { humanize } from "../../lib/labels";
import type { TaxReceipt } from "../../types/domain";

const STATUS_COLOR: Record<string, string> = {
  synced: "green",
  pending: "yellow",
  failed: "red",
  skipped: "gray",
};

export function TaxReceiptsPage() {
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<TaxReceipt | null>(null);

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<TaxReceipt>({
      queryKey: ["tax-receipts"],
      fetcher: taxReceiptsApi.list,
      filters: { status },
    });

  const statusQuery = useQuery({
    queryKey: ["tax-receipts-status"],
    queryFn: taxReceiptsApi.status,
  });

  const reissueMutation = useMutation({
    mutationFn: (ref: string) => taxReceiptsApi.reissue(ref),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: "Re-issue triggered. Refresh in a moment to see the result.",
      });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const columns: Column<TaxReceipt>[] = [
    {
      key: "custinvoiceno",
      header: "Invoice no.",
      render: (r) => <Code>{r.custinvoiceno}</Code>,
    },
    {
      key: "driverName",
      header: "Driver",
      render: (r) => r.driverName || <Text c="dimmed">—</Text>,
    },
    {
      key: "collectionMethod",
      header: "Collected via",
      render: (r) => humanize(r.collectionMethod ?? ""),
    },
    {
      key: "tripCount",
      header: "Trips",
      align: "right",
      render: (r) => r.tripCount ?? 1,
    },
    {
      key: "commissionAmount",
      header: "Commission",
      align: "right",
      render: (r) => formatMoney(r.commissionAmount),
    },
    {
      key: "bookingFeeAmount",
      header: "Booking fee",
      align: "right",
      render: (r) => formatMoney(r.bookingFeeAmount),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge color={STATUS_COLOR[r.status ?? ""] ?? "gray"} variant="light">
          {humanize(r.status ?? "")}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (r) => formatDateTime(r.createdAt),
    },
  ];

  return (
    <Stack>
      <PageHeader
        title="Tax Receipts"
        subtitle="VFD fiscal receipts for commission collected"
        actions={
          <Group gap="xs">
            <SearchInput
              value={search}
              onChange={onSearchChange}
              placeholder="Invoice no. or driver"
            />
            <Select
              placeholder="All statuses"
              data={["pending", "synced", "failed", "skipped"]}
              value={status}
              onChange={(val) => {
                setStatus(val ?? "");
                setPage(1);
              }}
              clearable
              w={160}
            />
          </Group>
        }
      />

      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        Receipts fiscalise <strong>Smart Safiri's own revenue only</strong> —
        commission and booking fee, itemised as two lines — and are addressed to
        the driver. They are issued when commission is actually{" "}
        <strong>collected</strong>, so an online or wallet trip fiscalises at
        completion while a cash trip waits until the driver settles. Cash
        commission that is never settled is never fiscalised.
      </Alert>

      {statusQuery.data && !statusQuery.data.enabled && (
        <Alert variant="light" color="yellow">
          The VFD integration is disabled — no receipts are being submitted.
        </Alert>
      )}
      {statusQuery.data?.enabled && !statusQuery.data.configured && (
        <Alert variant="light" color="orange">
          VFD is enabled but credentials are not configured; submissions will
          keep failing until FCODE and FCODE_TOKEN are set.
        </Alert>
      )}

      <DataTable<TaxReceipt>
        columns={columns}
        rows={result?.items ?? []}
        rowKey={(r) => r._id}
        loading={isLoading}
        error={error ? errorMessage(error) : null}
        page={page}
        totalPages={result?.totalPages}
        onPageChange={setPage}
        total={result?.total}
        onRowClick={setSelected}
        emptyLabel="No tax receipts yet"
      />

      <DetailDrawer
        opened={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.custinvoiceno ?? "Receipt"}
      >
        {selected && (
          <Stack>
            <DetailSection title="Fiscalisation">
              <DetailField label="Invoice no." value={selected.custinvoiceno} />
              <DetailField
                label="Receipt no."
                value={selected.receiptNumber ?? "—"}
              />
              <DetailField
                label="Status"
                value={humanize(selected.status ?? "")}
              />
              <DetailField
                label="Attempts"
                value={String(selected.attempts ?? 0)}
              />
              {selected.lastError && (
                <DetailField label="Last error" value={selected.lastError} />
              )}
              {selected.verificationUrl && (
                <DetailField
                  label="Verification"
                  value={
                    <a
                      href={selected.verificationUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open QR / verification link
                    </a>
                  }
                />
              )}
            </DetailSection>

            <DetailSection title="Subject">
              <DetailField
                label="Addressed to"
                value={humanize(selected.subjectType ?? "driver")}
              />
              <DetailField label="Driver" value={selected.driverName || "—"} />
              <DetailField label="Phone" value={selected.driverPhone || "—"} />
            </DetailSection>

            <DetailSection title="Amounts">
              <DetailField
                label="Commission"
                value={formatMoney(selected.commissionAmount)}
              />
              <DetailField
                label="Booking fee"
                value={formatMoney(selected.bookingFeeAmount)}
              />
              <DetailField
                label="Taxable total"
                value={formatMoney(selected.taxableAmount)}
              />
              <DetailField
                label="VAT (informational)"
                value={formatMoney(selected.vatAmount)}
              />
              <DetailField
                label="Trips covered"
                value={String(selected.tripCount ?? 1)}
              />
            </DetailSection>

            {selected.collectionRef && (
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                loading={reissueMutation.isPending}
                onClick={() => reissueMutation.mutate(selected.collectionRef!)}
              >
                Re-issue receipt
              </Button>
            )}
          </Stack>
        )}
      </DetailDrawer>
    </Stack>
  );
}
